import React, { useEffect, useRef, useState, useCallback } from 'react';
import './DigitSlot.css';

interface DigitSlotProps {
  targetDigit: string;
  isSpinning: boolean;
  onAnimationEnd?: () => void;
  delay?: number;
  spinDuration?: number;
  showPlaceholder?: boolean;
}

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

const DigitSlot: React.FC<DigitSlotProps> = ({
  targetDigit,
  isSpinning,
  onAnimationEnd,
  delay = 0,
  spinDuration = 2500,
  showPlaceholder = false,
}) => {
  const [currentDigit, setCurrentDigit] = useState<string>(targetDigit);
  const [nextDigit, setNextDigit] = useState<string>(targetDigit);
  const [isSliding, setIsSliding] = useState(false);
  const [animationState, setAnimationState] = useState<'idle' | 'spinning'>('idle');
  const [currentDelay, setCurrentDelay] = useState(50);
  const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const slideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sequenceRef = useRef<string[]>([]);
  const indexRef = useRef(0);
  const delaysRef = useRef<number[]>([]);

  // Generate sequence of digits ending at target
  const generateSequence = useCallback((endDigit: string): string[] => {
    const result: string[] = [];
    const targetIndex = DIGITS.indexOf(endDigit);

    // 4 full cycles of 0-9
    for (let cycle = 0; cycle < 4; cycle++) {
      for (let i = 0; i < 10; i++) {
        result.push(DIGITS[i]);
      }
    }

    // Final approach: 0 to target
    for (let i = 0; i <= targetIndex; i++) {
      result.push(DIGITS[i]);
    }

    return result;
  }, []);

  // Calculate delays for each step - constant speed at start, slow down at end
  const calculateDelays = useCallback((totalDigits: number, totalDuration: number): number[] => {
    const delays: number[] = [];
    const slowdownCount = 8; // Last 8 digits will slow down
    const slowdownStart = Math.max(0, totalDigits - slowdownCount);

    // Base delay for constant speed phase
    const baseDelay = 55;

    for (let i = 0; i < totalDigits; i++) {
      if (i < slowdownStart) {
        // Constant speed phase
        delays.push(baseDelay);
      } else {
        // Slowdown phase - exponentially increasing delays
        const progress = (i - slowdownStart) / slowdownCount;
        // Delay increases from baseDelay to ~400ms
        const slowDelay = baseDelay + Math.pow(progress, 1.5) * 350;
        delays.push(Math.round(slowDelay));
      }
    }

    // Scale to fit total duration
    const currentTotal = delays.reduce((sum, d) => sum + d, 0);
    const scale = totalDuration / currentTotal;

    return delays.map((d) => Math.round(d * scale));
  }, []);

  // Slide to next digit
  const slideToNext = useCallback(() => {
    if (indexRef.current >= sequenceRef.current.length) {
      setAnimationState('idle');
      setIsSliding(false);
      onAnimationEnd?.();
      return;
    }

    const nextDigitValue = sequenceRef.current[indexRef.current];
    const delay = delaysRef.current[indexRef.current];

    setNextDigit(nextDigitValue);
    setCurrentDelay(delay);
    setIsSliding(true);

    // After slide animation completes, update current and continue
    slideTimeoutRef.current = setTimeout(() => {
      setCurrentDigit(nextDigitValue);
      setIsSliding(false);
      indexRef.current++;

      // Small delay before next slide
      setTimeout(() => {
        slideToNext();
      }, 10);
    }, delay * 0.8); // Slide takes 80% of delay time
  }, [onAnimationEnd]);

  // Initialize with target digit
  useEffect(() => {
    if (!isSpinning && animationState === 'idle') {
      setCurrentDigit(targetDigit);
      setNextDigit(targetDigit);
    }
  }, [targetDigit, isSpinning, animationState]);

  // Start spinning animation
  useEffect(() => {
    if (isSpinning && animationState === 'idle') {
      delayTimeoutRef.current = setTimeout(() => {
        setAnimationState('spinning');

        const sequence = generateSequence(targetDigit);
        const delays = calculateDelays(sequence.length, spinDuration);

        sequenceRef.current = sequence;
        delaysRef.current = delays;
        indexRef.current = 0;

        slideToNext();
      }, delay);
    }

    return () => {
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
      }
      if (slideTimeoutRef.current) {
        clearTimeout(slideTimeoutRef.current);
      }
    };
  }, [
    isSpinning,
    delay,
    spinDuration,
    targetDigit,
    generateSequence,
    calculateDelays,
    slideToNext,
    animationState,
  ]);

  // Reset when spinning stops externally
  useEffect(() => {
    if (!isSpinning && animationState !== 'idle') {
      if (slideTimeoutRef.current) {
        clearTimeout(slideTimeoutRef.current);
      }
      setCurrentDigit(targetDigit);
      setNextDigit(targetDigit);
      setIsSliding(false);
      setAnimationState('idle');
    }
  }, [isSpinning, targetDigit, animationState]);

  // Show "X" placeholder when waiting for turn
  if (showPlaceholder && !isSpinning && animationState === 'idle') {
    return (
      <div className="digit-slot">
        <div className="digit-slot__window">
          <div className="digit-slot__digit digit-slot__digit--placeholder">X</div>
        </div>
      </div>
    );
  }

  return (
    <div className="digit-slot">
      <div className="digit-slot__window">
        <div
          className={`digit-slot__reel ${isSliding ? 'digit-slot__reel--sliding' : ''}`}
          style={
            isSliding
              ? ({ '--slide-duration': `${currentDelay * 0.8}ms` } as React.CSSProperties)
              : undefined
          }
        >
          <div className="digit-slot__digit">{nextDigit}</div>
          <div className="digit-slot__digit">{currentDigit}</div>
        </div>
      </div>
    </div>
  );
};

export default DigitSlot;
